/**
 * Environment Banner
 * Shows a small, unobtrusive indicator on staging/preview/dev deployments.
 * Helps testers and developers identify they are on a test environment.
 */

import React from 'react';
import { useState, useEffect } from 'react';

// Detect environment type
function getEnvironmentInfo(): { isPreview: boolean; isStaging: boolean; isLocalhost: boolean; label: string } {
  if (typeof window === 'undefined') {
    return { isPreview: false, isStaging: false, isLocalhost: false, label: '' };
  }

  const hostname = window.location.hostname;

  // Staging subdomain
  if (hostname === 'staging.subvention360.com') {
    return { isPreview: false, isStaging: true, isLocalhost: false, label: 'STAGING' };
  }

  // Vercel preview deployments
  if (hostname.includes('vercel.app')) {
    return { isPreview: true, isStaging: false, isLocalhost: false, label: 'PREVIEW' };
  }

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { isPreview: false, isStaging: false, isLocalhost: true, label: 'DEV' };
  }

  // Production - no banner
  return { isPreview: false, isStaging: false, isLocalhost: false, label: '' };
}

export const EnvironmentBanner: React.FC = () => {
  const [envInfo, setEnvInfo] = useState(() => getEnvironmentInfo());

  useEffect(() => {
    setEnvInfo(getEnvironmentInfo());
  }, []);

  // Don't show on production
  if (!envInfo.label) {
    return null;
  }

  // Color scheme based on environment
  const getBadgeStyles = () => {
    if (envInfo.isStaging) {
      return 'bg-orange-500 text-white';
    }
    if (envInfo.isPreview) {
      return 'bg-yellow-500 text-yellow-900';
    }
    if (envInfo.isLocalhost) {
      return 'bg-blue-500 text-white';
    }
    return 'bg-gray-500 text-white';
  };

  // Small fixed badge in the corner - non-dismissible, always visible
  return (
    <div
      className={`fixed top-1 left-1 px-2 py-0.5 rounded text-xs font-bold z-[100] ${getBadgeStyles()} opacity-80 hover:opacity-100 transition-opacity`}
      title="Ceci est un environnement de test"
    >
      {envInfo.label}
    </div>
  );
};

export default EnvironmentBanner;
